import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { IcoChevronRight16, IcoFileText16, IcoPlusSmall16 } from '@onefootprint/icons';
import type { OrgArtifact } from '@onefootprint/request-types/dashboard';
import { IconButton, Tooltip } from '@onefootprint/ui';
import * as Collapsible from '@radix-ui/react-collapsible';
import { cva, cx } from 'class-variance-authority';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getArtifactIcon from '@/utils/get-artifact-icon';
import { type ArtifactSelectorOptionId, GENERATED_SUMMARY_OPTION_ID } from '../header/types';

// A task node from the agent's plan
export type ArtifactPlanTask = {
  nodeId: string;
  label: string;
  completedAt?: string;
};

// Row label: judgments, rule results, and decision results show their CEL field key (the `.json`
// filename is an implementation detail); everything else (uploads, agent files) shows its human name.
export const artifactLabel = (artifact: OrgArtifact): string => artifact.metadata.kind === 'agent_graph_judgement' || artifact.metadata.kind === 'agent_graph_rule_result' || artifact.metadata.kind === 'agent_graph_decision_result' ? artifact.metadata.data.fieldKey : artifact.name;
const completionTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit'
});
const formatCompletionTime = (iso?: string) => iso ? completionTimeFormatter.format(new Date(iso)) : undefined;

// Shared props for the two list layouts.
type ArtifactListProps = {
  selectedOptionId: ArtifactSelectorOptionId;
  onSelect: (optionId: ArtifactSelectorOptionId) => void;
  onBlankDocument: () => void;
  summaryMatches: boolean;
  generatedSummaryLabel: string;
  forceOpen: boolean;
  /** Scalar judgment values by artifact id, shown as a tag on the matching judgment rows. */
  judgmentValues?: Record<string, string>;
  /** The run's inputs artifact, pinned as the first row. Absent when there are no inputs (or it's
   * filtered out by search). */
  inputsArtifact?: OrgArtifact;
};

// Grouped ("timeline × filesystem") layout: artifacts under their producing task node, in plan order.
export const GroupedArtifactList = t0 => {
  const $ = _c(30);
  const {
    taskGroups,
    otherArtifacts,
    selectedOptionId,
    onSelect,
    onBlankDocument,
    summaryMatches,
    generatedSummaryLabel,
    forceOpen,
    judgmentValues,
    inputsArtifact
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "artifactSidebar"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t1);
  let t2;
  if ($[1] !== inputsArtifact || $[2] !== onSelect || $[3] !== selectedOptionId) {
    t2 = <InputsRow artifact={inputsArtifact} selectedOptionId={selectedOptionId} onSelect={onSelect} />;
    $[1] = inputsArtifact;
    $[2] = onSelect;
    $[3] = selectedOptionId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== forceOpen || $[6] !== judgmentValues || $[7] !== onSelect || $[8] !== selectedOptionId || $[9] !== taskGroups) {
    let t4;
    if ($[11] !== forceOpen || $[12] !== judgmentValues || $[13] !== onSelect || $[14] !== selectedOptionId) {
      t4 = t5 => {
        const {
          task,
          items
        } = t5;
        return <Section key={task.nodeId} title={task.label} timestamp={formatCompletionTime(task.completedAt)} forceOpen={forceOpen}>{items.map(artifact => <ArtifactRow key={artifact.id} artifact={artifact} selectedOptionId={selectedOptionId} onSelect={onSelect} value={judgmentValues?.[artifact.id]} />)}</Section>;
      };
      $[11] = forceOpen;
      $[12] = judgmentValues;
      $[13] = onSelect;
      $[14] = selectedOptionId;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t3 = taskGroups.map(t4);
    $[5] = forceOpen;
    $[6] = judgmentValues;
    $[7] = onSelect;
    $[8] = selectedOptionId;
    $[9] = taskGroups;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[16] !== forceOpen || $[17] !== generatedSummaryLabel || $[18] !== judgmentValues || $[19] !== onBlankDocument || $[20] !== onSelect || $[21] !== otherArtifacts || $[22] !== selectedOptionId || $[23] !== summaryMatches || $[24] !== t) {
    t4 = (summaryMatches || otherArtifacts.length > 0) && <Section title={t(_temp)} action={<CreateBlankDocumentButton onClick={onBlankDocument} />} forceOpen={forceOpen}>{summaryMatches && <GeneratedSummaryRow label={generatedSummaryLabel} selected={selectedOptionId === GENERATED_SUMMARY_OPTION_ID} onSelect={onSelect} />}{otherArtifacts.map(artifact_0 => <ArtifactRow key={artifact_0.id} artifact={artifact_0} selectedOptionId={selectedOptionId} onSelect={onSelect} value={judgmentValues?.[artifact_0.id]} />)}</Section>;
    $[16] = forceOpen;
    $[17] = generatedSummaryLabel;
    $[18] = judgmentValues;
    $[19] = onBlankDocument;
    $[20] = onSelect;
    $[21] = otherArtifacts;
    $[22] = selectedOptionId;
    $[23] = summaryMatches;
    $[24] = t;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t2 || $[27] !== t3 || $[28] !== t4) {
    t5 = <div className="flex flex-col gap-2">{t2}{t3}{t4}</div>;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  return t5;
};

// Flat layout: an Artifacts section (documents + Generated Summary) and an Agent-judgments section.
export const FlatArtifactList = t0 => {
  const $ = _c(27);
  const {
    showDocuments,
    files,
    judgements,
    selectedOptionId,
    onSelect,
    onBlankDocument,
    summaryMatches,
    generatedSummaryLabel,
    forceOpen,
    judgmentValues,
    inputsArtifact
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "artifactSidebar"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t1);
  let t2;
  if ($[1] !== inputsArtifact || $[2] !== onSelect || $[3] !== selectedOptionId) {
    t2 = <InputsRow artifact={inputsArtifact} selectedOptionId={selectedOptionId} onSelect={onSelect} />;
    $[1] = inputsArtifact;
    $[2] = onSelect;
    $[3] = selectedOptionId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== files || $[6] !== forceOpen || $[7] !== generatedSummaryLabel || $[8] !== judgmentValues || $[9] !== onBlankDocument || $[10] !== onSelect || $[11] !== selectedOptionId || $[12] !== showDocuments || $[13] !== summaryMatches || $[14] !== t) {
    t3 = showDocuments && <Section title={t(_temp2)} action={<CreateBlankDocumentButton onClick={onBlankDocument} />} forceOpen={forceOpen}>{summaryMatches && <GeneratedSummaryRow label={generatedSummaryLabel} selected={selectedOptionId === GENERATED_SUMMARY_OPTION_ID} onSelect={onSelect} />}{files.map(artifact => <ArtifactRow key={artifact.id} artifact={artifact} selectedOptionId={selectedOptionId} onSelect={onSelect} value={judgmentValues?.[artifact.id]} />)}</Section>;
    $[5] = files;
    $[6] = forceOpen;
    $[7] = generatedSummaryLabel;
    $[8] = judgmentValues;
    $[9] = onBlankDocument;
    $[10] = onSelect;
    $[11] = selectedOptionId;
    $[12] = showDocuments;
    $[13] = summaryMatches;
    $[14] = t;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== forceOpen || $[17] !== judgements || $[18] !== judgmentValues || $[19] !== onSelect || $[20] !== selectedOptionId || $[21] !== t) {
    t4 = judgements.length > 0 && <Section title={t(_temp3)} forceOpen={forceOpen}>{judgements.map(artifact_0 => <ArtifactRow key={artifact_0.id} artifact={artifact_0} selectedOptionId={selectedOptionId} onSelect={onSelect} value={judgmentValues?.[artifact_0.id]} />)}</Section>;
    $[16] = forceOpen;
    $[17] = judgements;
    $[18] = judgmentValues;
    $[19] = onSelect;
    $[20] = selectedOptionId;
    $[21] = t;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t2 || $[24] !== t3 || $[25] !== t4) {
    t5 = <div className="flex flex-col gap-2">{t2}{t3}{t4}</div>;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
};
export const ArtifactRow = t0 => {
  const $ = _c(13);
  const {
    artifact,
    selectedOptionId,
    onSelect,
    value
  } = t0;
  let t1;
  if ($[0] !== artifact.mimeType) {
    t1 = getArtifactIcon(artifact.mimeType);
    $[0] = artifact.mimeType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== artifact) {
    t2 = artifactLabel(artifact);
    $[2] = artifact;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = selectedOptionId === artifact.id;
  let t4;
  if ($[4] !== artifact.id || $[5] !== onSelect) {
    t4 = () => onSelect(artifact.id);
    $[4] = artifact.id;
    $[5] = onSelect;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== value) {
    t5 = <Row icon={t1} label={t2} selected={t3} value={value} mono={true} onClick={t4} />;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = value;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
const GeneratedSummaryRow = t0 => {
  const $ = _c(6);
  const {
    label,
    selected,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect) {
    t1 = () => onSelect(GENERATED_SUMMARY_OPTION_ID);
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== selected || $[4] !== t1) {
    t2 = <Row icon={IcoFileText16} label={label} selected={selected} onClick={t1} />;
    $[2] = label;
    $[3] = selected;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

// The run's inputs, pinned as the first row (top-level, not under a task section). Renders nothing
// when there's no inputs artifact or search filtered it out.
const InputsRow = t0 => {
  const $ = _c(13);
  const {
    artifact,
    selectedOptionId,
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "artifactSidebar"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t1);
  if (!artifact) {
    return null;
  }
  let t2;
  if ($[1] !== artifact.mimeType) {
    t2 = getArtifactIcon(artifact.mimeType);
    $[1] = artifact.mimeType;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t(_temp4);
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = selectedOptionId === artifact.id;
  let t5;
  if ($[5] !== artifact.id || $[6] !== onSelect) {
    t5 = () => onSelect(artifact.id);
    $[5] = artifact.id;
    $[6] = onSelect;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <ul className="m-0 p-0 list-none"><Row icon={t2} label={t3} selected={t4} onClick={t5} /></ul>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const CreateBlankDocumentButton = t0 => {
  const $ = _c(9);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "artifactSidebar"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("artifact", t1);
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp5);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const label = t2;
  let t3;
  if ($[3] !== label || $[4] !== onClick) {
    t3 = <IconButton aria-label={label} className="max-md:size-9" icon={IcoPlusSmall16} size="tiny" variant="ghost" onClick={onClick} />;
    $[3] = label;
    $[4] = onClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label || $[7] !== t3) {
    t4 = <Tooltip content={label}>{t3}</Tooltip>;
    $[6] = label;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export const Section = t0 => {
  const $ = _c(29);
  const {
    title,
    action,
    timestamp,
    forceOpen,
    children
  } = t0;
  const [userOpen, setUserOpen] = useState(true);
  const open = forceOpen || userOpen;
  const t1 = forceOpen ? undefined : setUserOpen;
  const t2 = `${open ? "Collapse" : "Expand"} ${title}`;
  const t3 = open && "rotate-90";
  let t4;
  if ($[0] !== t3) {
    t4 = cx("transition-transform", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t4) {
    t5 = <IcoChevronRight16 className={t4} color="tertiary" />;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t2 || $[5] !== t5) {
    t6 = <button type="button" aria-label={t2} className="shrink-0 size-6 flex items-center justify-center rounded-(--icon-button-border-radius-tiny) outline-none transition-colors hover:bg-hover-control focus-visible:ring-1 focus-visible:ring-accent cursor-pointer disabled:cursor-default disabled:hover:bg-transparent max-md:relative max-md:before:absolute max-md:before:-inset-2">{t5}</button>;
    $[4] = t2;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== forceOpen || $[8] !== t6) {
    t7 = <Collapsible.Trigger asChild={true} disabled={forceOpen}>{t6}</Collapsible.Trigger>;
    $[7] = forceOpen;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== title) {
    t8 = <span title={title} className="text-label-3 text-tertiary truncate">{title}</span>;
    $[10] = title;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== timestamp) {
    t9 = timestamp && <time className="shrink-0 text-caption-3 text-quaternary tabular-nums whitespace-nowrap">{timestamp}</time>;
    $[12] = timestamp;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2">{t8}{t9}</div>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== action || $[18] !== t10 || $[19] !== t7) {
    t11 = <div className="pr-0.5 h-9 md:h-7 flex items-center gap-1">{t7}{t10}{action}</div>;
    $[17] = action;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== children || $[22] !== title) {
    t12 = <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-collapseUp data-[state=open]:animate-collapseDown"><ul aria-label={title} className="mt-1 mb-0 ml-2.5 pl-3.5 flex flex-col gap-0.5 list-none border-l border-tertiary">{children}</ul></Collapsible.Content>;
    $[21] = children;
    $[22] = title;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== open || $[25] !== t1 || $[26] !== t11 || $[27] !== t12) {
    t13 = <Collapsible.Root open={open} onOpenChange={t1} className="flex flex-col gap-1">{t11}{t12}</Collapsible.Root>;
    $[24] = open;
    $[25] = t1;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
const rowStyles = cva('w-full flex items-center gap-2 h-9 md:h-7 px-1.5 rounded-default no-underline text-left cursor-pointer outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset', {
  variants: {
    selected: {
      true: 'text-primary bg-senary/70 text-label-3',
      false: 'text-tertiary hover:text-primary hover:bg-hover text-body-3'
    }
  }
});
type RowProps = {
  icon: Icon;
  label: string;
  selected: boolean;
  /** Judgment value shown as a code tag at the far right. */
  value?: string;
  /** Render the label monospace — for code-y artifact identifiers (field keys, filenames), not the
   * friendly synthetic rows (Generated Summary, Inputs). */
  mono?: boolean;
  onClick: () => void;
};
const Row = t0 => {
  const $ = _c(18);
  const {
    icon: Icon,
    label,
    selected,
    value,
    mono,
    onClick
  } = t0;
  const t1 = selected ? "location" : undefined;
  let t2;
  if ($[0] !== selected) {
    t2 = rowStyles({
      selected
    });
    $[0] = selected;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Icon) {
    t3 = <Icon className="shrink-0" />;
    $[2] = Icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = mono && "font-mono";
  let t5;
  if ($[4] !== t4) {
    t5 = cx("min-w-0 truncate", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== label || $[7] !== t5) {
    t6 = <span className={t5}>{label}</span>;
    $[6] = label;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== value) {
    t7 = value && <span title={value} className="shrink-0 ml-auto max-w-[112px] truncate text-snippet-3 px-1 py-0.5 bg-secondary text-tertiary border border-tertiary rounded-sm">{value}</span>;
    $[9] = value;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onClick || $[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t6 || $[16] !== t7) {
    t8 = <li className="m-0 p-0"><button type="button" onClick={onClick} aria-current={t1} className={t2}>{t3}{t6}{t7}</button></li>;
    $[11] = onClick;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
function _temp($) {
  return $.groups.otherArtifacts;
}
function _temp2($) {
  return $.groups.documents;
}
function _temp3($_0) {
  return $_0.groups.judgements;
}
function _temp4($) {
  return $.inputs;
}
function _temp5($) {
  return $.createBlankDocument;
}